import { defineStore } from "pinia";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useAuthStore } from "./auth.store";

// Store do obsługi danych aktualnie zalogowanego użytkownika oraz preferencji
export const useUserStore = defineStore({
  id: "user",
  state: () => ({
    admin: null,          // Czy aktualny użytkownik ma uprawnienia admina?
    currentUser: null,    // Dane zalogowanego użytkownika
    preferences: null,    // Preferencje użytkownika
  }),
  actions: {
    // Resetowanie stanu użytkownika (wylogowanie, czyszczenie preferencji)
    reset() {
      this.currentUser = null;
      this.preferences = {};
    },
    // Aktualizacja danych użytkownika (np. imię, email)
    async update(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/update`,
          payload
        );
        return { result: updateRes, error: null };
      } catch (error) {
        console.log(error);
        return { result: null, error: error };
      }
    },
    // Aktualizacja ustawień/preferencji użytkownika
    async updateSettings(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/settings`,
          payload
        );
        return { result: updateRes, error: null };
      } catch (error) {
        console.log(error);
        return { result: null, error: error };
      }
    },
    // Zmiana hasła przez użytkownika
    async updatePassword(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/update-password`,
          payload
        );
        return { result: updateRes, error: null };
      } catch (error) {
        console.log(error);
        return { result: null, error: error };
      }
    },
    // Pobierz własne dane i preferencje użytkownika (jeśli jeszcze nie są załadowane)
    async self() {
      const auth = useAuthStore();

      if (!auth.token) { 
        return null;
      }

      if (this.currentUser === null) {
        // Pobierz dane użytkownika
        const userRes = await fetchWrapper.get(
          `${process.env.VUE_APP_API_URL}/users/self`
        );
        this.currentUser = userRes;

        // Obsługa numeru telefonu (wydzielenie prefiksu)
        if (this.currentUser.phoneNumber) {
          this.currentUser.phoneNumberDir = this.currentUser.phoneNumber.substr(0,3);
          this.currentUser.phoneNumber = this.currentUser.phoneNumber.substr(3);
        } else {
          this.currentUser.phoneNumberDir = "+48";
          this.currentUser.phoneNumber = "";
        }
        // Pobierz preferencje
        const prefRes = await fetchWrapper.get(
          `${process.env.VUE_APP_API_URL}/users/self/settings`
        );
        this.preferences = prefRes;
      }
      return this.currentUser;
    },
    // Sprawdź i ustaw, czy użytkownik ma uprawnienia administratora (ROLE_7)
    async roles() {
      if (this.currentUser === null) {
        await this.self();
      }
    
      if (!this.currentUser || !this.currentUser.roles) {
        this.admin = false;
        return;
      }
    
      if (this.admin === null) {
        const hasRole7 = this.currentUser.roles[0]?.permissions?.some(
          (perm) => perm.name === "ROLE_7"
        );
        this.admin = !!hasRole7;
      }
    },    
    // Sprawdzenie czy użytkownik ma dostęp do określonych zasobów/funkcjonalności (np. WantedList, Stat itd.)
    hasAccess(items) {
      this.roles(); // Upewnij się, że role są wczytane

      const itemArr = items.split(",");

      for (const itemCheck in itemArr) {
        if (this.getItemAccess(itemArr[itemCheck])) {
          return true; // Ma dostęp do przynajmniej jednej funkcji
        }
      }
      return false;
    },
    // Mapowanie nazw uprawnień do konkretnych ról systemowych (np. ROLE_13 = BlackList)
    getItemAccess(item) {
      if (item === "CurrentMeasurements") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za bieżące pomiary.
        return this.checkRole("ROLE_1");
      }
      if (item === "ArchiveMeasurements") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za archiwalne pomiary.
        return this.checkRole("ROLE_2");
      }
      if (item === "Stat") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za statystyki.
        return this.checkRole("ROLE_4");
      }
      if (item === "UsersAdmin") {
        // Umożliwia przeglądanie logów aktywności wszystkich zwykłych użytkowników za wyjątkiem użytkowników specjalnych.
        return this.checkRole("ROLE_7");
      }
      if (item === "SelfPersonal") {
        // Umożliwia wyświetlenie danych personalnych użytkownika.
        return this.checkRole("ROLE_10");
      }
      if (item === "PasswordChange") {
        // Umożliwia użytkownikowi zmianę jego hasła
        return this.checkRole("ROLE_11");
      }
      if (item === "Roles") {
        // Umożliwia dostęp do wszystkich ról, które istnieją w systemie, za wyjątkiem tych specjalnych, które są przeznaczone dla specjalnych użytkowników.
        return this.checkRole("ROLE_19");
      }
      if (item === "Vms") {
        // Umożliwia dostęp do zasobów, które zwracają dane stacji preselekcji.
        // return this.checkRole("ROLE_27");
        return this.checkRole("ROLE_3");
      }
      if (item === "SelfConfig") {
        // Umożliwia dostęp do zasobów, które zapisują i odczytują parametry konfiguracyjne zalogowanego użytkownika..
        // return this.checkRole("ROLE_30");
        return this.checkRole("ROLE_10") || this.checkRole("ROLE_11");
      }
      if (item === "Anonymization") {
        // Umożliwia wyłączenie nietrwałej anonimizacji dla użytkowników posiadających to uprawnienie.
        return this.checkRole("ROLE_37");
      }
      if (item === "WantedList") {
        // Umożliwia dostęp do zasobów związanych z monitorowanymi pojazdami.
        return this.checkRole("ROLE_12");
      }

      /*
        Role - panel admina:
        7, 8, 12, 17, 19, 20, 22, 27, 33, 34
      */

      if (item === 'Admin') {
        return this.checkRole("ROLE_7") || this.checkRole("ROLE_8") || this.checkRole("ROLE_12") || this.checkRole("ROLE_13") || this.checkRole("ROLE_14") || this.checkRole("ROLE_15") || this.checkRole("ROLE_17") || this.checkRole("ROLE_19") || this.checkRole("ROLE_20") || this.checkRole("ROLE_22") || this.checkRole("ROLE_27") || this.checkRole("ROLE_33") || this.checkRole("ROLE_34") || this.checkRole("ROLE_38");
      }

      if (item === 'UserManagement') {
        return this.checkRole("ROLE_19") || this.checkRole("ROLE_20");
      }

      if (item === 'Tickets') {
        return this.checkRole("ROLE_38");
      }

      if (item === 'BlackList') {
        return this.checkRole("ROLE_13");
      }

      if (item === 'WhiteList') {
        return this.checkRole("ROLE_15");
      }

      if (item === 'Contacts') {
        return this.checkRole("ROLE_14");
      }

      return false;
    },
    // Sprawdź, czy aktualny użytkownik ma określoną rolę (np. ROLE_13, ROLE_7)
    checkRole(id) {
      return this.currentUser?.roles?.[0]?.permissions?.some(
        (role) => role.name === id
      ) || false;
    },    
  },
});
